import React, { ChangeEvent } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Switch,
  TextField,
  Divider,
  IconButton,
} from "@material-ui/core";
import { IFeature, FeatureParameters, useFeatures } from "../contexts/FeatureFlags";
import { useState } from "react";
import { MoreVert as MoreVertIcon } from "@material-ui/icons";
import { useLocalStorage } from "../services";

const useStyles = makeStyles((theme: Theme) => ({
  sectionTitleMenuItemRoot: {
    "&:hover": { backgroundColor: "inherit" },
    fontSize: "0.8rem",
    textTransform: "uppercase",
    cursor: "auto",
    pointerEvents: "none",
    color: theme.palette.text.disabled,
  },
  hr: {
    border: 0,
    height: "1px",
    backgroundColor: theme.palette.grey[300],
  },
  secondary: {
    marginLeft: "1.618rem",
  },
  textField: { width: "10rem" },
  listItemIconRoot: { minWidth: "unset" },
}));

const FeatureFlagOption = ({
  feature,
  toggleOptStatus,
  setParameters,
  onToggleAccountMenu,
}: {
  feature: IFeature;
  toggleOptStatus: (name: string) => void;
  setParameters: (featureName: string, parameters: FeatureParameters) => void;
  onToggleAccountMenu?: () => void;
}) => {
  const { name, active, parameters } = feature;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [params, setParams] = useLocalStorage(feature.name, Object.values(parameters));
  const classes = useStyles();

  const toggleDialogOpen = () =>
    setDialogOpen((state) => {
      if (state) {
        setParameters(
          feature.name,
          Object.fromEntries(params.map((param) => [param.name, param]))
        );
      }
      return !state;
    });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    parameterName: string
  ) => {
    e.persist();
    setParams((params) =>
      params.map((param) =>
        param.name === parameterName ? { ...param, value: e.target.value as any } : param
      )
    );
  };

  const handleToggleDialog = () => {
    toggleDialogOpen();
    onToggleAccountMenu && onToggleAccountMenu();
  };

  return (
    <ListItem key={name} dense>
      <ListItemIcon classes={{ root: classes.listItemIconRoot }}>
        <Switch
          edge="start"
          size="small"
          checked={active}
          onChange={() => toggleOptStatus(name)}
        />
      </ListItemIcon>
      <ListItemText primary={name} />
      {params.length ? (
        <>
          <IconButton
            className={classes.secondary}
            size="small"
            edge="end"
            onClick={handleToggleDialog}
          >
            <MoreVertIcon />
          </IconButton>
          <Dialog
            open={dialogOpen}
            onClose={handleToggleDialog}
            fullWidth
            maxWidth={"sm"}
          >
            <DialogTitle>Feature Config {name}</DialogTitle>
            <DialogContent>
              {params.map((parameter, i) => {
                if (parameter.type === "string") {
                  if (!parameter.value) parameter.value = parameter.default || "";
                  return (
                    <TextField
                      key={i}
                      label={parameter.name}
                      value={parameter.value}
                      id={parameter.name}
                      {...(i === 0 ? { autoFocus: true } : {})}
                      fullWidth
                      autoFocus
                      onChange={(e) => handleChange(e, parameter.name)}
                    />
                  );
                }
                return null;
              })}
              <DialogActions>
                <Button onClick={handleToggleDialog}>Save</Button>
              </DialogActions>
            </DialogContent>
          </Dialog>
        </>
      ) : null}
    </ListItem>
  );
};

const FeatureFlagOptions = ({
  onToggleAccountMenu,
}: {
  onToggleAccountMenu?: () => void;
}) => {
  const { toggleOptStatus, optionalFeatures, setParameters } = useFeatures();
  if (!toggleOptStatus || !setParameters) return null;
  return (
    <>
      <ListSubheader>{"Experimental Features"}</ListSubheader>
      {optionalFeatures?.map((feature) => (
        <FeatureFlagOption
          key={feature.name}
          {...{ feature, toggleOptStatus, setParameters, onToggleAccountMenu }}
        />
      ))}
      <Divider />
    </>
  );
};

export default FeatureFlagOptions;
